'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
// Import-free predicate, shared with error.tsx and every segment boundary. A
// pure function carries no graph behind it, which is what makes it an
// acceptable dependency in the file that must render when nothing else can.
import { isTransientNetworkError } from '@/lib/errors/transient-network';
// Import-free as well: the retry latch that survives a remount, and the
// chunk-failure report Sentry's inbound filter does not drop.
import {
  boundaryRetryAvailable,
  isChunkLoadError,
  noteBoundaryRetry,
  releaseBoundaryRetry,
  recoverFromChunkFailure,
  RETRY_GRACE_MS,
  silentRetry,
  tryAgain,
} from '@/lib/errors/boundary-recovery';
// Import-free as well: whether a live call is holding off a reload, which a
// refresh that cannot connect would turn into (MUCKA-WEB-CH).
import { onReloadsReleased, reloadsHeld } from '@/lib/reload-guard';
// Import-free as well: whether one of our own full-page loads is already
// replacing the page, so a chunk boundary never starts a second (MUCKA-WEB-CV).
import { notePageLeaving, pageLeaving } from '@/lib/page-leaving';
// Import-free as well: the mark on our <body> that tells the hydration
// detector this boundary took the page, so it is not filed twice (MUCKA-WEB-A2).
import { GLOBAL_BOUNDARY_ATTR, GLOBAL_BOUNDARY_VALUE } from '@/lib/errors/global-boundary-mark';

/** Spread on both of this boundary's bodies. */
const BODY_MARK = { [GLOBAL_BOUNDARY_ATTR]: GLOBAL_BOUNDARY_VALUE };

/**
 * Root-level error boundary. Catches errors thrown inside the root
 * layout itself (segment-level error.tsx files handle errors inside
 * their own segments). Must inline html+body and styles because the
 * root layout has failed by the time this renders.
 *
 * Added in response to TestFlight reports of blank screens that
 * survived app kills + phone restarts: any uncaught client render
 * error now lands on a recoverable surface with a "Reset & sign in"
 * escape hatch that clears localStorage + sessionStorage so a wedged
 * persisted state can't keep the user stuck.
 */
/**
 * localStorage keys the "Reset & sign in" wipe must leave alone.
 *
 * Written as literals rather than imported from `src/lib/offline/sync.ts` on
 * purpose: this component is the last thing standing when the app has already
 * failed to render, so it takes no dependency that could itself throw or drag
 * the offline module into the root bundle. `__tests__/global-error-keeps-offline-pointer.test.ts`
 * asserts these still match the real constants.
 */
const KEEP_THROUGH_RESET = ['mucka_offline_active_account'];

export default function GlobalError({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  /** Next's own refresh-then-reset: the only one that fetches the page again. */
  retry?: () => void;
}) {
  /**
   * One silent re-render for a dropped connection, before we take the whole
   * document away from somebody.
   *
   * 🚨 This boundary is the expensive one. It replaces `<html>`, which unmounts
   * the offline banner, the saved-work overlay and the sync loop along with the
   * page — so a WKWebView losing its connection for a second cost a plumber in
   * a van the entire app and left `reset()`, which re-runs the same tree
   * against the same conditions, as the only way out. The segment boundaries
   * have retried since #1983 and this one did not, which is why the single
   * MUCKA-WEB-35 event AFTER that shipped is tagged `error_boundary: global`.
   *
   * Bounded to ONE attempt, for the same reason as everywhere else: a boundary
   * that retries on a loop turns a deterministic failure into a spinner nobody
   * can escape, and a deterministic failure is exactly the kind worth showing.
   *
   * 🚨 The bound is held outside this component: Next unmounts it to render
   * the page again, so a second failure mounts a new one with fresh state, and
   * state alone never stopped (MUCKA-WEB-A2). A failed chunk is never
   * retried by re-rendering — `React.lazy` throws its cached failure back.
   */
  // Set once the retry has been in flight for RETRY_GRACE_MS: still a
  // connection, not a fault, so it gets "Reconnecting…" and no report.
  const [stillWaiting, setStillWaiting] = useState(false);
  // A call is holding reloads, so the retry waits for it to end before it
  // fetches (`silentRetry`). Read once at mount, like the retry latch.
  const [callHeld] = useState(() => reloadsHeld());
  const [retryAvailable] = useState(() => boundaryRetryAvailable('global'));
  const chunk = isChunkLoadError(error);
  const transient = !chunk && isTransientNetworkError(error);
  const willRetry = transient && retryAvailable;
  const waitingOnCall = willRetry && callHeld;
  // A chunk's second chance is a reload, once (MUCKA-WEB-CV — see
  // `recoverFromChunkFailure`). Blank until it is spent, refused or overdue.
  const [chunkScreen, setChunkScreen] = useState(false);
  const chunkHandled = useRef<unknown>(null);

  useEffect(() => {
    if (!willRetry) return;
    noteBoundaryRetry('global');
    // Fetch the page again, not merely render it: `reset()` alone re-reads
    // the dead stream that threw (MUCKA-WEB-CH). During a call, once it ends.
    const cancel = silentRetry(retry, reset, { reloadsHeld, onReloadsReleased });
    // What un-blanks the screen if the retry neither lands nor fails.
    // `retry()` is a transition, and this screen stays mounted until the
    // fresh page arrives (see RETRY_GRACE_MS).
    const timer = setTimeout(() => setStillWaiting(true), RETRY_GRACE_MS);
    return () => {
      clearTimeout(timer);
      cancel();
      // Held for as long as this screen is up, however long the fetch takes.
      releaseBoundaryRetry('global');
    };
  }, [willRetry, retry, reset]);

  useEffect(() => {
    if (willRetry) return;
    // Sentry's chunk-load inbound filter drops a captured ChunkLoadError, so
    // report one as a message it keeps.
    if (chunk) {
      // Once per error: StrictMode's rehearsal must not spend the reload.
      if (chunkHandled.current === error) return;
      chunkHandled.current = error;
      recoverFromChunkFailure(Sentry, 'global', error, reloadsHeld, () => setChunkScreen(true), { pageLeaving, notePageLeaving });
      return;
    }
    // Tagged, not bare. MUCKA-WEB-35 sat on the board as an unresolved
    // "TypeError: Load failed" that took a code read to identify as this
    // boundary doing exactly its job — catching a navigation into a dead
    // network and offering recovery in place. `navigator.onLine` is the split
    // that makes the issue triageable from the list: offline is the designed
    // path, online is a real defect. Same reasoning as the app_state tag on
    // the iOS content-process reports (#1496) — a fact in context is
    // unsearchable, and opening every event is how a real fault stays buried
    // under routine ones.
    Sentry.captureException(error, {
      tags: {
        error_boundary: 'global',
        // 'recovered' never reaches here (we return above); the tag marks the
        // ones the user actually SAW, so the ratio stays readable in Sentry.
        boundary_outcome: transient ? 'shown_after_retry' : 'shown',
        // `onLine === false` is conclusive; true only means the interface is
        // up, so it is reported as-is rather than as "was online".
        navigator_online: typeof navigator === 'undefined' ? 'unknown' : String(navigator.onLine),
        boundary_route: typeof window === 'undefined' ? 'unknown' : window.location.pathname,
      },
    });
  }, [error, willRetry, transient, chunk]);

  // A reload that has not replaced the page by now is not going to on its own.
  useEffect(() => {
    if (!chunk || chunkScreen) return;
    const timer = setTimeout(() => setChunkScreen(true), RETRY_GRACE_MS);
    return () => clearTimeout(timer);
  }, [chunk, chunkScreen]);

  const handleHardReset = () => {
    try {
      // 🚨 Carry the offline pointers across the wipe.
      //
      // A blanket clear() takes `mucka_offline_active_account` with it, and
      // every offline surface keys off that one string to find its data —
      // the snapshot, the saved jobs, and the certificate records an engineer
      // has filled in but not yet synced. The IndexedDB rows survive the
      // clear; the pointer to them does not, so the work becomes unreachable
      // and the app reports "Nothing saved yet" over a cellar's worth of
      // unsynced certificates. That is the worst possible thing to do to
      // someone who reached this page by accident.
      //
      // An account id cannot wedge a render, so nothing about the escape hatch
      // is weakened by keeping it. The sync stamp is deliberately NOT kept —
      // losing it just means the next sync runs immediately, which is the
      // behaviour you want after a reset anyway.
      const keep = KEEP_THROUGH_RESET.map(
        (k) => [k, window.localStorage.getItem(k)] as const,
      );
      window.localStorage.clear();
      window.sessionStorage.clear();
      for (const [k, v] of keep) if (v !== null) window.localStorage.setItem(k, v);
    } catch { /* private mode / quota — keep going */ }
    // Headed to /login so the user re-authenticates rather than
    // looping back into whatever route threw.
    window.location.href = '/login';
  };

  // The retry frame. Unlike a segment boundary this component IS the document,
  // so it cannot return null — it renders the same shell with nothing in it,
  // which reads as a blink rather than a broken page.
  //
  // Past RETRY_GRACE_MS the retry is still a connection, not a fault, so it
  // says so — with no "Try again", because one is already running.
  //
  // A chunk reloading the page gets the same empty shell: the document is
  // about to be replaced. `stillWaiting` is never set on that path, so it
  // stays empty, and past RETRY_GRACE_MS the error screen takes over.
  if (willRetry || (chunk && !chunkScreen)) {
    return (
      <html lang="en">
        <body
          {...BODY_MARK}
          style={{
            margin: 0,
            background: '#161616',
            minHeight: '100vh',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: '#eae9e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          {(stillWaiting || waitingOnCall) && (
            <div style={{ maxWidth: 380, textAlign: 'center' }}>
              <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
                {waitingOnCall ? 'This will load after your call' : 'Reconnecting…'}
              </h1>
              <p style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.7, margin: 0 }}>
                {waitingOnCall
                  ? <>Mucka dropped while it was loading. It&apos;ll load again as soon as
                    the call ends — nothing you&apos;ve saved has been lost.</>
                  : <>The signal dropped while Mucka was loading. It&apos;ll appear as soon as
                    it&apos;s back — nothing you&apos;ve saved has been lost.</>}
              </p>
            </div>
          )}
        </body>
      </html>
    );
  }

  return (
    <html lang="en">
      <body
        {...BODY_MARK}
        style={{
          margin: 0,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          background: '#161616',
          color: '#eae9e8',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 380, textAlign: 'center' }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              borderRadius: '50%',
              border: '2px solid #ff5a3c',
              background: 'rgba(255,90,60,0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff5a3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 8px' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.7, margin: '0 0 24px' }}>
            Mucka hit an unexpected error. Try reloading — if you keep landing here, tap Reset to clear your local session.
          </p>
          {error?.digest && (
            <p style={{ fontSize: 11, opacity: 0.4, marginBottom: 16, fontFamily: 'monospace' }}>
              Ref: {error.digest}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              // A chunk that would not load needs a fresh document; reset()
              // re-renders the lazy component, which fetches nothing.
              onClick={chunk ? () => window.location.reload() : () => tryAgain(retry, reset, reloadsHeld)}
              style={{
                background: '#ff5a3c',
                color: '#fff',
                border: 'none',
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
              }}
            >
              Try again
            </button>
            <button
              onClick={handleHardReset}
              style={{
                background: 'transparent',
                color: '#eae9e8',
                border: '1px solid rgba(234,233,232,0.2)',
                padding: '11px 20px',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
              }}
            >
              Reset &amp; sign in
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
